import {
  isSentinel,
  sentinelLabel,
  formatCurrency,
  formatPercent,
  enumLabel,
  formatBenefitAmount,
  formatDefinitionOfEarnings,
  DEFINITION_OF_DISABILITY_LABELS,
  INCLUSION_LABELS,
  EMPLOYER_CONTRIBUTION_LABELS,
  formatRateGuarantee,
  formatPreExistingConditionMonthsDetail,
} from "./shared";

const NOT_INCLUDED = sentinelLabel("notIncluded");

export type LtdFieldFormatter = (row: Record<string, unknown>) => string;

/** Maximum Amount — same enum as STD. */
const formatMaximumAmount: LtdFieldFormatter = (row) => {
  const type = row["maximumAmountType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "unlimited") return "Unlimited";
  if (type === "dollarAmount")
    return formatCurrency(row["maximumAmountDollarAmount"]) || "";
  if (type === "dollarAmountNotToExceedPercentageOfPredisabilityEarnings") {
    const dollar = formatCurrency(row["maximumAmountDollarAmount"]);
    const pct = formatPercent(row["maximumAmountPercent"]);
    if (dollar && pct) return `Lesser of ${dollar} or ${pct} earnings`;
    return dollar || pct || "";
  }
  return "";
};

/** Guaranteed Issue — dollarAmount → $X; notIncluded → NOT_INCLUDED. */
const formatGuaranteedIssue: LtdFieldFormatter = (row) => {
  const type = row["guaranteedIssueType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  if (type === "dollarAmount")
    return formatCurrency(row["guaranteedIssueAmount"]) || "";
  return "";
};

/** Own Occupation Period. */
const OWN_OCC_LABELS: Record<string, string> = {
  yearsOwnOccupation: "Years Own Occupation",
  monthsOwnOccupation: "Months Own Occupation",
  ownOccupationOnly: "Own Occupation Only",
  notIncluded: NOT_INCLUDED,
};

const formatOwnOccupationPeriod: LtdFieldFormatter = (row) => {
  const type = row["ownOccupationPeriodType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  const label = typeof type === "string" ? (OWN_OCC_LABELS[type] ?? "") : "";
  if (type === "yearsOwnOccupation" || type === "monthsOwnOccupation") {
    const amount = row["ownOccupationPeriodAmount"];

    if (amount != null) {
      const unit =
        type === "yearsOwnOccupation"
          ? amount === 1
            ? "Year"
            : "Years"
          : amount === 1
            ? "Month"
            : "Months";

      return `${amount} ${unit}`;
    }
  }
  return label;
};

/** Gainful earnings test — percentage → X%; notApplicable/notIncluded. */
const gainfulEarningsTest =
  (typeKey: string, amountKey: string): LtdFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    if (type === "notApplicable") return sentinelLabel("notApplicable");

    if (type === "percentage") return formatPercent(row[amountKey]) || "";
    return "";
  };

/** Elimination Period — days only (the sole non-sentinel type the backend accepts). */
const formatEliminationPeriod: LtdFieldFormatter = (row) => {
  const type = row["eliminationPeriodType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  const days = row["eliminationPeriodDays"];
  if (days == null) return "";
  if (type === "days") return `${String(days)} days`;
  return String(days);
};

const BENEFIT_DURATION_LABELS: Record<string, string> = {
  ssnra: "SSNRA",
  ssnraWithRbd: "SSNRA with RBD",
  rbdWithSsnra: "RBD with SSNRA",
  adea1WithSsnra: "ADEA 1 with SSNRA",
  adea1: "ADEA 1",
  adea2: "ADEA 2",
  adea3: "ADEA 3",
  adea4: "ADEA 4",
  twoYearAdea: "2-Year ADEA",
  threeYearAdea: "3-Year ADEA",
  fiveYearAdea: "5-Year ADEA",
  tenYearAdeaSchedule: "10-Year ADEA Schedule",
  reducingBenefitDuration: "Reducing Benefit Duration",
  ssAdea: "SS/ADEA",
  toAge65: "To Age 65",
  toAge67: "To Age 67",
  laterOfAgeOrSsnra: "Later of Age or SSNRA",
  lesserOfRbdOrNumberOfYears: "Lesser of RBD or Years",
  yearDuration: "Years",
  notIncluded: NOT_INCLUDED,
};

const formatBenefitDuration: LtdFieldFormatter = (row) => {
  const type = row["benefitDurationType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  const label =
    typeof type === "string" ? (BENEFIT_DURATION_LABELS[type] ?? "") : "";
  const amount = row["benefitDurationAmount"];
  if (amount == null) return label;
  if (type === "laterOfAgeOrSsnra") return `${label}: Age ${amount}`;
  if (type === "lesserOfRbdOrNumberOfYears" || type === "yearDuration") {
    const unit = amount === 1 ? "Year" : "Years";
    return `${label}: ${amount} ${unit}`;
  }
  return label;
};

const LIMITATION_LABELS: Record<string, string> = {
  years: "Years",
  months: "Months",
  noLimit: "No Limit",
  payableSameAsAnyOtherDisability: "Payable Same as Any Other Disability",
  included: "Included",
  notIncluded: NOT_INCLUDED,
};

/** Generic limitation field (otherLimitations, mentalIllness, substanceAbuse). */
const limitation =
  (typeKey: string, amountKey: string): LtdFieldFormatter =>
  (row) => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type as string);
    const label =
      typeof type === "string" ? (LIMITATION_LABELS[type] ?? "") : "";
    if (type === "years" || type === "months") {
      const amount = row[amountKey];

      if (amount != null) {
        const unit =
          type === "years"
            ? amount === 1
              ? "Year"
              : "Years"
            : amount === 1
              ? "Month"
              : "Months";

        return `${amount} ${unit}`;
      }
    }
    return label;
  };

/**
 * Minimum Amount — dollarAmount → $X, percentageOfSalary → X%, sentinels
 * (notDisclosed) pass through. Same pattern as STD's Minimum Benefit
 * (CD-2552).
 */
const formatMinimumAmount: LtdFieldFormatter = (row) => {
  const type = row["minimumAmountType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "percentageOfSalary")
    return formatPercent(row["minimumAmount"]) || "";
  if (type === "dollarAmount")
    return formatCurrency(row["minimumAmount"]) || "";
  return "";
};

const formatPreExistingCondition: LtdFieldFormatter = (row) => {
  const type = row["preExistingConditionType"];
  if (isSentinel(type)) return sentinelLabel(type as string);
  if (type === "waived") return "Waived";
  if (type === "none") return "None";
  if (type !== "months") return "";
  const detail = row["preExistingConditionMonthsDetail"];
  return typeof detail === "string" && detail
    ? formatPreExistingConditionMonthsDetail(detail)
    : "";
};

const REHAB_LABELS: Record<string, string> = {
  mandatory: "Mandatory",
  mandatoryWithEnhanced: "Mandatory with Enhanced",
  voluntary: "Voluntary",
  voluntaryWithEnhanced: "Voluntary with Enhanced",
  enhanced: "Enhanced",
  notIncluded: NOT_INCLUDED,
};

export const ltdFormatters: Record<string, LtdFieldFormatter> = {
  benefitAmount: formatBenefitAmount,
  maximumAmount: formatMaximumAmount,
  guaranteedIssue: formatGuaranteedIssue,
  ownOccupationPeriod: formatOwnOccupationPeriod,
  gainfulEarningsTestOwnOccupation: gainfulEarningsTest(
    "gainfulEarningsTestOwnOccupationType",
    "gainfulEarningsTestOwnOccupationAmount",
  ),
  gainfulEarningsTestAnyOccupation: gainfulEarningsTest(
    "gainfulEarningsTestAnyOccupationType",
    "gainfulEarningsTestAnyOccupationAmount",
  ),
  eliminationPeriod: formatEliminationPeriod,
  benefitDuration: formatBenefitDuration,
  minimumAmount: formatMinimumAmount,
  definitionOfEarnings: formatDefinitionOfEarnings,
  otherLimitations: limitation(
    "otherLimitationsType",
    "otherLimitationsAmount",
  ),
  mentalIllness: limitation("mentalIllnessType", "mentalIllnessAmount"),
  substanceAbuse: limitation("substanceAbuseType", "substanceAbuseAmount"),
  preExistingCondition: formatPreExistingCondition,
  definitionOfDisability: enumLabel(
    "definitionOfDisability",
    DEFINITION_OF_DISABILITY_LABELS,
  ),
  rehab: enumLabel("rehab", REHAB_LABELS),
  cola: enumLabel("cola", INCLUSION_LABELS),
  conversion: enumLabel("conversion", INCLUSION_LABELS),
  waiverOfPremium: enumLabel("waiverOfPremium", INCLUSION_LABELS),
  employerContribution: enumLabel(
    "employerContribution",
    EMPLOYER_CONTRIBUTION_LABELS,
  ),
  rateGuarantee: formatRateGuarantee,
};

export const formatLtdField = (
  fieldId: string,
  row: Record<string, unknown>,
): string | undefined => {
  const formatter = ltdFormatters[fieldId];
  return formatter ? formatter(row) : undefined;
};
